import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Palette, Shield, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const LandingPage = () => {
  const navigate = useNavigate();

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "AI-Powered Preview",
      description: "See your car in any wrap instantly using cutting-edge AI visualisation"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Premium Finishes",
      description: "Choose from matte, gloss, chrome, carbon fibre, and custom patterns"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Professional Quality",
      description: "Get an accurate preview before committing to your perfect wrap"
    }
  ];

  return (
    <div className="min-h-screen bg-premier-navy relative overflow-hidden">
      <div className="noise-overlay" />
      
      <section 
        className="hero-bg min-h-screen flex items-center justify-center relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80')"
        }}
      >
        <div className="absolute inset-0 hero-gradient z-[2]" />
        
        <nav className="absolute top-0 left-0 right-0 z-10 px-6 md:px-12 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-end">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Button 
                variant="ghost" 
                className="font-mono text-xs tracking-widest uppercase text-white/60 hover:text-white"
                onClick={() => navigate('/admin')}
                data-testid="admin-link"
              >
                Admin
              </Button>
            </motion.div>
          </div>
        </nav>

        <div className="relative z-[3] text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8"
          >
            <img 
              src="/logo.png" 
              alt="Premier Signs & Wraps" 
              className="h-32 md:h-44 lg:h-52 w-auto mx-auto drop-shadow-2xl"
            />
          </motion.div>
          
          <motion.p
            {...fadeUp}
            transition={{ delay: 0.2, ...fadeUp.transition }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-premier-blue mb-6"
          >
            AI-Powered Car Wrap Visualisation
          </motion.p>
          
          <motion.h1 
            {...fadeUp}
            transition={{ delay: 0.3, ...fadeUp.transition }}
            className="font-unbounded font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-[0.9] mb-8"
          >
            Transform<br />
            <span className="text-glow">Your Ride</span>
          </motion.h1>
          
          <motion.p
            {...fadeUp}
            transition={{ delay: 0.4, ...fadeUp.transition }}
            className="font-manrope text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-12"
          >
            Upload a photo of your car and preview it in any wrap style using AI. 
            See the transformation before you commit.
          </motion.p>
          
          <motion.div
            {...fadeUp}
            transition={{ delay: 0.5, ...fadeUp.transition }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              onClick={() => navigate('/configurator')}
              className="bg-premier-blue hover:bg-premier-purple text-white font-bold px-8 py-6 text-lg group transition-all"
              data-testid="start-customizing-btn"
            >
              Start Customising
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              data-testid="learn-more-btn"
            >
              Learn More
            </Button>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[3]"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1 h-2 bg-premier-blue rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      <section id="features" className="py-24 px-6 md:px-12 bg-premier-purple relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-premier-blue mb-4">
              Why Choose Premier Signs & Wraps
            </p>
            <h2 className="font-unbounded font-bold text-3xl md:text-5xl tracking-tight uppercase">
              Next-Gen Wrap Preview
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group relative bg-premier-navy/50 border border-white/10 p-8 hover:border-premier-blue/50 transition-all duration-500 rounded-lg"
              >
                <div className="text-premier-blue mb-6">{feature.icon}</div>
                <h3 className="font-unbounded font-bold text-xl mb-4 group-hover:text-premier-blue transition-colors">
                  {feature.title}
                </h3>
                <p className="font-manrope text-white/60">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 bg-premier-navy relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1759166499946-9ab9e7776d41?w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-premier-navy via-premier-navy/80 to-premier-navy" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-premier-blue mb-4">
                Ready to Transform?
              </p>
              <h2 className="font-unbounded font-bold text-3xl md:text-5xl tracking-tight uppercase mb-6">
                See Your Car<br />In A New Light
              </h2>
              <p className="font-manrope text-white/60 mb-8 max-w-md">
                Our AI-powered visualiser lets you see exactly how your car will look 
                with different wrap options. No guesswork, just stunning previews.
              </p>
              <Button
                onClick={() => navigate('/configurator')}
                className="bg-premier-blue hover:bg-premier-purple text-white font-bold px-8 py-6 text-lg group transition-all"
                data-testid="try-now-btn"
              >
                Try It Now
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden md:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-premier-blue/20 blur-3xl rounded-full" />
                <img 
                  src="https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=600&q=80"
                  alt="Wrapped Car Preview"
                  className="relative rounded-lg border border-white/10"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 md:px-12 bg-premier-purple border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="Premier Signs & Wraps" 
              className="h-12 w-auto"
            />
          </div>
          <p className="font-mono text-xs text-white/40">
            &copy; 2025 Premier Signs & Wraps Ltd. AI-Powered Car Wrap Visualisation.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
